<script setup>
import { computed } from 'vue'
import AppButton from '@/components/atoms/AppButton'
import AppMarkdown from '@/components/atoms/AppMarkdown'
import HelpHubChatAttachments from './HelpHubChatAttachments.vue'
import HelpHubChatReleaseNotes from './HelpHubChatReleaseNotes.vue'
import { isHandoffNote, isOwnMessage, isSystemLine, messageChoices, replyText, senderLabel, supportMessageTime } from '@/helpers/support'

/**
 * One turn of the conversation: the dealer's words as a tinted bubble on the right, or a
 * reply under its sender's name, on a card that can hold prose, a structured answer (the
 * release notes) or the AI's follow-up choices.
 */
const props = defineProps({
  message: { type: Object, required: true },
  // A choice can only be answered while nothing else is in flight.
  busy: { type: Boolean, default: false },
})

defineEmits(['choose'])

// The note under the handoff, in the design's words whatever line the service posted (Figma 30603:646799).
const HANDOFF_NOTE = 'A member of the support team will reply here.\n\nYou can close this window and come back anytime — we’ll email you when they reply.'

const own = computed(() => isOwnMessage(props.message))
const who = computed(() => senderLabel(props.message))
const time = computed(() => supportMessageTime(props.message.createdAt))
const handoff = computed(() => isHandoffNote(props.message))
// The service's own line is a muted note, as the widget draws it, not a reply.
const system = computed(() => isSystemLine(props.message))
// An agent's reply arrives as Markdown; so does the AI's, without its internal markers.
const content = computed(() => {
  if (own.value) return props.message.content ?? ''
  return handoff.value ? HANDOFF_NOTE : replyText(props.message)
})
// The AI narrows a question to a closed set. Without these the dealer cannot answer it.
const choices = computed(() => messageChoices(props.message))
const attachments = computed(() => props.message.attachments ?? [])
</script>

<template>
  <li v-if="own" class="flex flex-col items-end gap-y-1">
    <!-- Figma 29855:72883 draws no name over the dealer's own bubble; screen readers still get one. -->
    <span class="sr-only">{{ who }}</span>

    <!-- The squared corner marks the sender's side: bottom-right on the dealer's own bubble (Figma draws it bottom-left). -->
    <div class="max-w-[85%] rounded-xl rounded-br-[4px] bg-tint-1 px-3 py-2.5 shadow-sm">
      <p v-if="content" class="whitespace-pre-wrap break-words text-sm font-medium text-white" v-text="content" />
      <HelpHubChatAttachments v-if="attachments.length" :attachments="attachments" class="[&_a]:border-white/30" />
    </div>

    <span v-if="time" class="text-xs text-gray-400" v-text="time" />
  </li>

  <li v-else class="flex flex-col gap-y-2">
    <div class="flex items-center gap-x-2">
      <span class="flex size-6 shrink-0 items-center justify-center rounded-xl bg-gray-100">
        <!-- 18.46 × 16, so it is sized on both axes rather than squared. -->
        <Icon icon="cdp:support-bot" class="h-4 w-[18.46px] shrink-0" :class="system ? 'text-gray-400' : 'text-tint-1'" />
      </span>

      <span class="min-w-0 truncate text-xs font-semibold text-gray-500">{{ who }}</span>
    </div>

    <p v-if="system" class="whitespace-pre-wrap break-words rounded-xl bg-gray-100 px-3 py-2 text-xs italic text-gray-500" v-text="content" />

    <div v-else class="rounded-xl border border-gray-200 bg-gray-50 p-3 shadow-sm">
      <HelpHubChatReleaseNotes v-if="message.card?.type === 'releaseNotes'" :notes="message.card.notes" />
      <AppMarkdown v-else-if="content" :source="content" />

      <HelpHubChatAttachments v-if="attachments.length" :attachments="attachments" />

      <!-- The answer to a choice is sent as its label: the dealer's own words, as if typed. -->
      <div v-if="choices.length" class="mt-3 flex flex-wrap gap-2">
        <AppButton
          v-for="choice in choices"
          :key="choice.value"
          variant="outlined"
          size="sm"
          :disabled="busy"
          @click="$emit('choose', choice.label)"
        >
          {{ choice.label }}
        </AppButton>
      </div>
    </div>

    <span v-if="time" class="text-xs text-gray-400" v-text="time" />
  </li>
</template>
